import { yupResolver } from '@hookform/resolvers/yup';
import EditIcon from '@mui/icons-material/Edit';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import Paper from '@mui/material/Paper';
import Snackbar from '@mui/material/Snackbar';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import React, { useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { InferType } from 'yup';
import { aboutMeSchema } from '../../../../validation/clientProfile.schemas';

type AboutMeFormValues = InferType<typeof aboutMeSchema>;

interface AboutMeSectionProps {
  bio: string;
  onSave: (bio: string) => void;
}

const MAX_CHARS = 300;

const AboutMeSection: React.FC<AboutMeSectionProps> = ({ bio, onSave }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);

  const { control, handleSubmit, reset, watch } = useForm<AboutMeFormValues>({
    resolver: yupResolver(aboutMeSchema),
    defaultValues: { bio },
  });

  const currentBio = (watch('bio') ?? '') as string;

  const handleEdit = () => {
    reset({ bio });
    setIsEditing(true);
  };

  const handleCancel = () => {
    reset({ bio });
    setIsEditing(false);
  };

  const onSubmit = (values: AboutMeFormValues) => {
    onSave(values.bio ?? '');
    setIsEditing(false);
    setSnackbarOpen(true);
  };

  return (
    <Paper sx={{ p: 3, borderRadius: 2, mb: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h6" fontWeight={600}>
          About Me
        </Typography>
        {!isEditing && (
          <IconButton onClick={handleEdit} size="small" sx={{ color: '#178B8E' }}>
            <EditIcon />
          </IconButton>
        )}
      </Box>

      {isEditing ? (
        <Box component="form" onSubmit={handleSubmit(onSubmit)}>
          <Controller
            name="bio"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                value={field.value ?? ''}
                multiline
                rows={4}
                fullWidth
                placeholder="Tell sitters a bit about yourself and your pets..."
                inputProps={{ maxLength: MAX_CHARS }}
                onChange={(e) => field.onChange(e.target.value.slice(0, MAX_CHARS))}
              />
            )}
          />
          <Box
            sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 1 }}
          >
            <Typography variant="caption" color="text.secondary">
              {currentBio.length}/{MAX_CHARS}
            </Typography>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <Button onClick={handleCancel} variant="outlined" sx={{ textTransform: 'none' }}>
                Cancel
              </Button>
              <Button
                type="submit"
                variant="contained"
                sx={{ textTransform: 'none', backgroundColor: '#178B8E' }}
              >
                Save
              </Button>
            </Box>
          </Box>
        </Box>
      ) : (
        <Typography variant="body1" color={bio ? 'text.primary' : 'text.secondary'}>
          {bio || 'Tell sitters a bit about yourself and your pets...'}
        </Typography>
      )}

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={3000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity="success" onClose={() => setSnackbarOpen(false)}>
          About me updated!
        </Alert>
      </Snackbar>
    </Paper>
  );
};

export default AboutMeSection;
